import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  calculateAllocation,
  checkEmptyAllocation,
  checkZeroAllocation,
  filterBeneficiaries,
} from 'enrollment/utils/common/Utils';
import { EsignatureDto } from '../../../queries/useBenefitPlansQuery/types';

export type BeneficiaryTypes = {
  allocation: string;
  coverage_line_id?: number;
  id?: number;
  uniq_id?: number;
  name: string;
  primary_beneficiary: boolean;
  relationship: string;
};

export interface BeneficiaryData {
  orgBenefitId: number;
  beneficiaries: BeneficiaryTypes[];
  primaryTotalAllocation: number;
  secondaryTotalAllocation: number;
  primaryEmptyAllocation: boolean;
  secondaryEmptyAllocation: boolean;
  primaryZeroAllocation: boolean;
  secondaryZeroAllocation: boolean;
  coverageFields: {
    req_volume?: any;
    carrier_logo_url?: any;
    carrierName: string;
    esign_data?: EsignatureDto;
  };
}
interface BeneficiaryState {
  beneficiaryData: BeneficiaryData[];
}

const initialState: BeneficiaryState = {
  beneficiaryData: [],
};
const beneficiarySlice = createSlice({
  name: 'Beneficiary',
  initialState,
  reducers: {
    setAllBeneficiaryData: (state, { payload }: PayloadAction<any>) => {
      return {
        ...state,
        beneficiaryData: [...payload],
      };
    },

    updateBenefitBeneficiaryData: (
      state,
      {
        payload: { orgBenefitId, updatedBeneficiaries, updatedEsign },
      }: PayloadAction<any>
    ) => {
      const updatedBenefitPlans = state.beneficiaryData.map(
        (beneficiaryData) => {
          if (beneficiaryData?.orgBenefitId === orgBenefitId) {
            return {
              ...beneficiaryData,
              beneficiaries: updatedBeneficiaries,
              coverageFields: {
                ...beneficiaryData.coverageFields,
                esign_data: updatedEsign,
              },
            };
          }
          return beneficiaryData;
        }
      );
      return {
        ...state,
        beneficiaryData: updatedBenefitPlans,
      };
    },

    addNewBeneficiary: (
      state,
      { payload: { orgBenefitId, beneficiaryAdded } }: PayloadAction<any>
    ) => {
      const updatedBeneficiaryData = state.beneficiaryData?.map(
        (beneficiaryData) => {
          if (beneficiaryData?.orgBenefitId !== orgBenefitId) {
            return beneficiaryData;
          }

          const updatedBeneficiaries = [
            ...beneficiaryData?.beneficiaries,
            beneficiaryAdded,
          ];

          return {
            ...beneficiaryData,
            beneficiaries: updatedBeneficiaries,
            primaryTotalAllocation: calculateAllocation(
              filterBeneficiaries(updatedBeneficiaries, true)
            ),
            secondaryTotalAllocation: calculateAllocation(
              filterBeneficiaries(updatedBeneficiaries, false)
            ),
            primaryEmptyAllocation: checkEmptyAllocation(
              filterBeneficiaries(updatedBeneficiaries, true)
            ),
            secondaryEmptyAllocation: checkEmptyAllocation(
              filterBeneficiaries(updatedBeneficiaries, false)
            ),
            primaryZeroAllocation: checkZeroAllocation(
              filterBeneficiaries(updatedBeneficiaries, true)
            ),
            secondaryZeroAllocation: checkZeroAllocation(
              filterBeneficiaries(updatedBeneficiaries, false)
            ),
          };
        }
      );
      return { ...state, beneficiaryData: updatedBeneficiaryData };
    },

    deleteBeneficiary: (
      state,
      { payload: { orgBenefitId, beneficiaryId } }: PayloadAction<any>
    ) => {
      const updatedBenefitPlans = state.beneficiaryData.map(
        (beneficiaryData) => {
          if (beneficiaryData?.orgBenefitId === orgBenefitId) {
            const objIndex = beneficiaryData?.beneficiaries.findIndex(
              (obj) => obj.uniq_id === parseInt(beneficiaryId)
            );
            const updatedBeneficiaries = beneficiaryData?.beneficiaries.filter(
              (beneficiary, index) => {
                return index !== objIndex;
              }
            );

            return {
              ...beneficiaryData,
              beneficiaries: updatedBeneficiaries,
              primaryTotalAllocation: calculateAllocation(
                filterBeneficiaries(updatedBeneficiaries, true)
              ),
              secondaryTotalAllocation: calculateAllocation(
                filterBeneficiaries(updatedBeneficiaries, false)
              ),
              primaryEmptyAllocation: checkEmptyAllocation(
                filterBeneficiaries(updatedBeneficiaries, true)
              ),
              secondaryEmptyAllocation: checkEmptyAllocation(
                filterBeneficiaries(updatedBeneficiaries, false)
              ),
              primaryZeroAllocation: checkZeroAllocation(
                filterBeneficiaries(updatedBeneficiaries, true)
              ),
              secondaryZeroAllocation: checkZeroAllocation(
                filterBeneficiaries(updatedBeneficiaries, false)
              ),
            };
          }
          return beneficiaryData;
        }
      );
      return { ...state, beneficiaryData: updatedBenefitPlans };
    },

    updateBeneficiaryAllocation: (
      state,
      { payload: { orgBenefitId, value, fieldName } }: PayloadAction<any>
    ) => {
      const updatedBenefitPlans = state.beneficiaryData.map(
        (beneficiaryData) => {
          if (beneficiaryData?.orgBenefitId !== orgBenefitId) {
            return beneficiaryData;
          }
          const beneficiaryId = fieldName.split('-')[1];

          const updatedBeneficiaries = beneficiaryData?.beneficiaries.map(
            (beneficiary) => {
              if (beneficiary.uniq_id === parseInt(beneficiaryId)) {
                return { ...beneficiary, allocation: value };
              }
              return beneficiary;
            }
          );

          return {
            ...beneficiaryData,
            beneficiaries: updatedBeneficiaries,
            primaryTotalAllocation: calculateAllocation(
              filterBeneficiaries(updatedBeneficiaries, true)
            ),
            secondaryTotalAllocation: calculateAllocation(
              filterBeneficiaries(updatedBeneficiaries, false)
            ),
            primaryEmptyAllocation: checkEmptyAllocation(
              filterBeneficiaries(updatedBeneficiaries, true)
            ),
            secondaryEmptyAllocation: checkEmptyAllocation(
              filterBeneficiaries(updatedBeneficiaries, false)
            ),
            primaryZeroAllocation: checkZeroAllocation(
              filterBeneficiaries(updatedBeneficiaries, true)
            ),
            secondaryZeroAllocation: checkZeroAllocation(
              filterBeneficiaries(updatedBeneficiaries, false)
            ),
          };
        }
      );

      return { ...state, beneficiaryData: updatedBenefitPlans };
    },
  },
});

const { reducer, actions } = beneficiarySlice;

// Actions
export const {
  setAllBeneficiaryData,
  addNewBeneficiary,
  deleteBeneficiary,
  updateBeneficiaryAllocation,
  updateBenefitBeneficiaryData,
} = actions;

export const beneficiaryReducer = reducer;
